import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../cn'
import { Search, X, FileText, SearchX } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useLanguage } from '../../providers/LanguageContext'

export interface SearchResultItem {
  id: string
  title: string
  snippet?: string
  url?: string
  icon?: React.ReactNode
}

export interface SearchOverlayProps {
  open: boolean
  onClose: () => void
  onSearch?: (query: string) => void
  /**
   * Fires on every keystroke (no debounce — consumer applies its own).
   * Wires the overlay's input value out so a parent can drive
   * search-as-you-type by feeding `results` back in.
   */
  onQueryChange?: (query: string) => void
  /**
   * Fires when the user clicks an item in `results`. When provided, the
   * overlay calls this AND `onClose()` so the consumer can navigate
   * (typically `react-router-dom`'s `navigate(url)`) without a full
   * reload. When omitted, the result's `<a href={url}>` handles
   * navigation natively.
   */
  onResultClick?: (item: SearchResultItem) => void
  placeholder?: string
  searchLabel?: string
  /**
   * Results to show. When provided as-you-type, the overlay renders
   * them inline (gated on `query.trim().length > 0`). An empty array +
   * non-empty query renders the "no results" state.
   */
  results?: SearchResultItem[]
  /** When true, show "no results" state (animated). */
  hasSearched?: boolean
  className?: string
}

export function SearchOverlay({
  open,
  onClose,
  onSearch,
  onQueryChange,
  onResultClick,
  placeholder,
  searchLabel,
  results: resultsProp,
  hasSearched: _hasSearched = false,
  className
}: SearchOverlayProps) {
  const { t } = useLanguage()
  const effectivePlaceholder = placeholder ?? t('search.overlay.placeholder')
  const effectiveSearchLabel = searchLabel ?? t('search.overlay.searchLabel')
  const [query, setQuery] = React.useState('')
  const [submitted, setSubmitted] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (open) {
      setSubmitted(false)
      setQuery('')
      onQueryChange?.('')
      const t = requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
      return () => cancelAnimationFrame(t)
    }
  }, [open, onQueryChange])

  React.useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    setSubmitted(true)
    if (q) onSearch?.(q)
  }

  const handleQueryChange = (next: string) => {
    setQuery(next)
    onQueryChange?.(next)
  }

  const clearQuery = () => handleQueryChange('')

  // Search-as-you-type: when consumer drives `results` based on query,
  // render them whenever query is non-empty AND results is defined
  // (empty array → no-results state; populated → list). Submitted-gated
  // empty array stays as the legacy submit-only fallback for consumers
  // that don't supply onQueryChange.
  const showAsYouType = onQueryChange != null && query.trim().length > 0
  const results = resultsProp ?? (submitted && query.trim() ? [] : undefined)
  const showNoResults =
    Array.isArray(results) &&
    results.length === 0 &&
    query.trim().length > 0 &&
    (showAsYouType || submitted)
  const showResults = Array.isArray(results) && results.length > 0

  if (!open) return null

  const overlay = (
    <div
      className={cn('search-overlay-backdrop', className)}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="search-overlay-panel" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className="search-bar-large-wrap">
          <div className="search-bar-large">
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder={effectivePlaceholder}
              className="search-bar-large-input focus-visible:ring-0"
              aria-label={effectivePlaceholder}
              autoComplete="off"
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={clearQuery}
                className="search-bar-large-clear"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <Button type="submit" variant="primary" className="search-bar-large-btn">
              <Search className="h-4 w-4" />
              {effectiveSearchLabel}
            </Button>
          </div>
        </form>

        <div className="search-overlay-results">
          {showNoResults && (
            <div className="search-no-results">
              <div className="search-no-results-icon" aria-hidden>
                <SearchX className="h-12 w-12" strokeWidth={1.5} />
              </div>
              <p className="search-no-results-title">{t('search.overlay.noResults', { query: query.trim() })}</p>
              <p className="search-no-results-hint">{t('search.overlay.tryDifferent')}</p>
            </div>
          )}
          {showResults && (
            <div className="search-results-list">
              <p className="search-results-heading">{t('search.overlay.topResults')}</p>
              {results!.map((item, i) => (
                <a
                  key={item.id}
                  href={item.url ?? '#'}
                  className={cn('search-result-item', `search-result-stagger-${Math.min(i, 20)}`)}
                  onClick={(e) => {
                    // When the consumer wires a click handler (typical
                    // SPA case — react-router's `navigate(url)`),
                    // prevent the native anchor navigation, fire the
                    // callback, and close the overlay so the consumer
                    // sees a clean unmount path. Without onResultClick,
                    // fall through to native href navigation.
                    if (onResultClick) {
                      e.preventDefault()
                      onResultClick(item)
                      onClose()
                    } else if (!item.url) {
                      e.preventDefault()
                    }
                  }}
                >
                  <span className="search-result-icon">
                    {item.icon ?? <FileText className="h-4 w-4" />}
                  </span>
                  <span className="search-result-content">
                    <span className="search-result-title">{item.title}</span>
                    {item.snippet && (
                      <span className="search-result-snippet search-result-snippet-doc">
                        {t('search.overlay.documentation')}{item.snippet}
                      </span>
                    )}
                  </span>
                </a>
              ))}
            </div>
          )}
          {!showResults && !showNoResults && (
            <p className="search-overlay-hint">{t('search.overlay.hint')}</p>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}
